/**
 * API Configuration
 * Centralized configuration for all API endpoints and base URLs
 */

type RuntimeEnv = 'development' | 'test' | 'production';

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1', '0.0.0.0'];
const TEST_HOST_KEYWORDS = ['test', 'testing', 'staging', '-stg'];

const isLocalHostname = (hostname: string): boolean => {
  return (
    LOCAL_HOSTNAMES.includes(hostname) ||
    hostname.endsWith('.local') ||
    hostname.endsWith('.localhost')
  );
};

const isTestHostname = (hostname: string): boolean => {
  return TEST_HOST_KEYWORDS.some((keyword) => hostname.includes(keyword));
};

const GLOBAL_DEBUG_FLAG = '__api_base_resolution__';

const detectRuntimeEnvironment = (): RuntimeEnv => {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const hostname = window.location.hostname.toLowerCase();

    if (isLocalHostname(hostname)) {
      return 'development';
    }

    if (isTestHostname(hostname)) {
      return 'test';
    }

    // For any other hostname (including production domains and Vercel previews),
    // default to production so APIs always hit the backend service.
    return 'production';
  }

  const envFromProcess = (process.env.REACT_APP_ENV || process.env.NODE_ENV || '').toLowerCase();

  if (envFromProcess === 'test' || envFromProcess === 'testing') {
    return 'test';
  }

  if (envFromProcess === 'prod' || envFromProcess === 'production') {
    return 'production';
  }

  return 'development';
};

const getEnvironmentFlags = () => {
  const runtimeEnvironment = detectRuntimeEnvironment();
  return {
    runtimeEnvironment,
    isDevelopment: runtimeEnvironment === 'development',
    isProduction: runtimeEnvironment === 'production',
    isTest: runtimeEnvironment === 'test',
  };
};

const logBaseUrlResolution = (details: {
  environment: RuntimeEnv;
  hostname?: string;
  customBaseUrl?: string | null;
  selectedBaseUrl: string;
}) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const lastLog = (window as any)[GLOBAL_DEBUG_FLAG];
    const signature = JSON.stringify(details);
    if (lastLog !== signature) {
      // eslint-disable-next-line no-console
      console.log('[API_CONFIG] Base URL resolved', {
        ...details,
        timestamp: new Date().toISOString(),
      });
      (window as any)[GLOBAL_DEBUG_FLAG] = signature;
    }
  } catch {
    // Ignore logging failures
  }
};

const stripQuotes = (value?: string) => {
  if (!value) return value;
  return value.replace(/^['"]+|['"]+$/g, '');
};

const normalizeBaseUrl = (url?: string) => {
  if (!url) return url;
  const cleaned = stripQuotes(url);
  return cleaned ? cleaned.replace(/\/$/, '') : cleaned;
};

const configuredBaseUrl = normalizeBaseUrl(process.env.REACT_APP_API_BASE_URL);
const configuredFallbackBaseUrl = normalizeBaseUrl(process.env.REACT_APP_API_FALLBACK_URL);

const resolveCustomBaseUrl = () => configuredBaseUrl || configuredFallbackBaseUrl;

// API Base URLs configuration
export const API_CONFIG = {
  // Base URLs for different environments
  BASE_URLS: {
    development: '', // Use proxy in development (handled by UmiJS proxy)
    test: resolveCustomBaseUrl() || 'https://test.backend.flashgreen.io',
    production: resolveCustomBaseUrl() || 'https://backend.flashgreen.io',
  },

  // Force HTTPS in production
  forceHttps(): boolean {
    const isProduction = process.env.NODE_ENV === 'production';
    const isHttps = window.location.protocol === 'https:';
    return isProduction || isHttps;
  },

  // Get current base URL based on environment
  getBaseURL(): string {
    const customBaseUrl = resolveCustomBaseUrl();
    if (customBaseUrl) {
      const { runtimeEnvironment } = getEnvironmentFlags();
      logBaseUrlResolution({
        environment: runtimeEnvironment,
        hostname: typeof window !== 'undefined' ? window.location.hostname : undefined,
        customBaseUrl,
        selectedBaseUrl: customBaseUrl,
      });
      return customBaseUrl;
    }

    const { isDevelopment, isProduction, isTest, runtimeEnvironment } = getEnvironmentFlags();
    const hostname = typeof window !== 'undefined' ? window.location.hostname : undefined;
    let selectedBaseUrl = this.BASE_URLS.development;

    // In development, use empty string to utilize proxy
    if (isDevelopment || (!isProduction && !isTest)) {
      selectedBaseUrl = this.BASE_URLS.development;
    } else if (isTest) {
      selectedBaseUrl = this.BASE_URLS.test;
    } else if (isProduction) {
      selectedBaseUrl = this.BASE_URLS.production;
    }

    // Safety net for non-local hostnames without explicit base URL
    if (!selectedBaseUrl && hostname && !isLocalHostname(hostname.toLowerCase())) {
      selectedBaseUrl = this.BASE_URLS.production;
    }

    logBaseUrlResolution({
      environment: runtimeEnvironment,
      hostname,
      customBaseUrl,
      selectedBaseUrl,
    });

    return selectedBaseUrl;
  },

  // API endpoints
  ENDPOINTS: {
    // Auth endpoints
    AUTH: {
      LOGIN: '/api/auth/login',
      LOGOUT: '/api/auth/logout',
      CURRENT_USER: '/api/user/current',
      REFRESH: '/api/auth/refresh',
      PERMISSIONS: '/api/auth/permissions',
      CHANGE_PASSWORD: '/api/auth/change-password',
    },

    // Notification center
    NOTIFICATION: {
      LIST: '/api/notifications',
      MARK_READ: '/api/notifications/read',
      MARK_DISPLAYED: '/api/notifications/displayed',
      MARK_SINGLE: (id: string) => `/api/notifications/${id}/read`,
    },

    // User management
    USER: {
      LIST: '/api/user',
      CREATE: '/api/user',
      UPDATE: (id: string) => `/api/user/${id}`,
      DELETE: (id: string) => `/api/user/${id}`,
      PROFILE: '/api/user/profile',
    },

    // Order management
    ORDER: {
      LIST: '/api/orders',
      CREATE: '/api/orders',
      UPDATE: (id: string) => `/api/orders/${id}`,
      DELETE: (id: string) => `/api/orders/${id}`,
      DETAILS: (id: string) => `/api/orders/${id}`,
      BY_LOCATION: (locationId: string) => `/api/orders/location/${locationId}`,
      BY_PAYMENT_STATUS: (status: string) => `/api/orders/payment-status/${status}`,
      BY_CHANNEL: (channel: string) => `/api/orders/channel/${channel}`,
      BY_DATE_RANGE: '/api/orders/date-range',
      EXPORT: '/api/orders/export',
      RECONCILIATION_ANALYZE: '/api/orders/reconciliation/analyze',
      // Order items
      ITEMS: {
        LIST: (orderId: string) => `/api/orders/${orderId}/items`,
        CREATE: (orderId: string) => `/api/orders/${orderId}/items`,
        UPDATE: (itemId: string) => `/api/orders/items/${itemId}`,
        DELETE: (itemId: string) => `/api/orders/items/${itemId}`,
      },
      // Discounts
      DISCOUNTS: {
        LIST: (orderId: string) => `/api/orders/${orderId}/discounts`,
        CREATE: (orderId: string) => `/api/orders/${orderId}/discounts`,
        DELETE: (orderId: string, discountUsageId: string) =>
          `/api/orders/${orderId}/discounts/${discountUsageId}`,
      },
      // Refunds
      REFUNDS: (orderId: string) => `/api/orders/${orderId}/refunds`,
      REFUND: (orderId: string) => `/api/orders/${orderId}/refund`,
      // Operations
      OPERATIONS: (orderId: string) => `/api/orders/${orderId}/operations`,
    },

    // Wholesale order management
    WHOLESALE_ORDER: {
      LIST: '/api/wholesale-orders',
      CREATE: '/api/wholesale-orders',
      UPDATE: (id: string) => `/api/wholesale-orders/${id}`,
      DETAILS: (id: string) => `/api/wholesale-orders/${id}`,
      EXPORT: '/api/wholesale-orders/export',
      PRICE_SUGGESTION: '/api/wholesale-orders/price-suggestion',
      PAYMENT: (id: string) => `/api/wholesale-orders/${id}/payment`,
      SHIP: (id: string) => `/api/wholesale-orders/${id}/ship`,
      ITEMS: {
        LIST: (orderId: string) => `/api/wholesale-orders/${orderId}/items`,
      },
      DISCOUNTS: {
        LIST: (orderId: string) => `/api/wholesale-orders/${orderId}/discounts`,
      },
      REFUNDS: (orderId: string) => `/api/wholesale-orders/${orderId}/refunds`,
      OPERATIONS: (orderId: string) => `/api/wholesale-orders/${orderId}/operations`,
    },

    ONLINE_ORDER: {
      LIST: '/api/orders/online',
      FOLLOWUP_LIST: '/api/orders/online/followup',
      MARK_FOLLOWED_UP_BATCH: '/api/orders/online/followup/batch',
      MARK_FOLLOWED_UP: (id: string) => `/api/orders/online/${id}/followup`,
      COMPLETE_FOLLOWUP_PICKUP: (id: string) => `/api/orders/online/${id}/followup/complete-pickup`,
      REFUND: (id: string) => `/api/orders/online/${id}/refund`,
      CASH_REFUND: (id: string) => `/api/orders/online/${id}/cash-refund`,
      REFRESH_REFUND: (id: string) => `/api/orders/online/${id}/refund/refresh`,
      SYNC_REFUNDS: '/api/orders/online/refunds/sync',
      DETAILS: (id: string) => `/api/orders/online/${id}`,
      FULFILLMENT_TIMELINE: (id: string) => `/api/orders/online/${id}/fulfillment-timeline`,
      SHIPPING_FEE_RULES: '/api/orders/online/shipping-fee-rules',
    },

    ONLINE_ORDER_EMAIL_NOTIFICATION: {
      LIST: '/api/orders/online/email-notifications',
    },

    // Product management
    PRODUCT: {
      LIST: '/api/product',
      FEATURED: '/api/product/featured',
      CREATE: '/api/product',
      CREATE_BATCH: '/api/product/batch',
      UPDATE: (id: string) => `/api/product/${id}`,
      DELETE: (id: string) => `/api/product/${id}`,
      DETAILS: (id: string) => `/api/product/${id}`,
      BATCH_DETAILS: '/api/product/batch/details',
      SEARCH_BY_NAME: '/api/product/search/name',
      SEARCH_BY_BARCODE: '/api/product/search/barcode',
      BY_CATEGORY: (categoryId: number) => `/api/product/category/${categoryId}`,
      BY_BRAND: (brandId: number) => `/api/product/brand/${brandId}`,
      BY_REGION: (regionId: number) => `/api/product/region/${regionId}`,
      BY_STATUS: (status: string) => `/api/product/status/${status}`,
      BY_STORAGE: (storageCondition: string) => `/api/product/storage/${storageCondition}`,
      BY_BARCODE: (barcode: string) => `/api/product/barcode/${barcode}`,
      EXPIRING_SOON: '/api/product/expiring-soon',
      DUPLICATE_BARCODES: '/api/product/duplicate-barcodes',
      NO_BARCODE_LIST: '/api/product/no-barcode',
      MARK_NO_BARCODE: (id: string) => `/api/product/${id}/no-barcode`,
      UNMARK_NO_BARCODE: (id: string) => `/api/product/${id}/no-barcode`,
      GENERATE_BARCODE: (id: string) => `/api/product/${id}/generate-barcode`,
      BARCODE_JUDGMENT: (id: string) => `/api/product/${id}/barcode-judgment`,
      REVEAL_COST: (id: string) => `/api/product/${id}/reveal-cost`,
      REVEAL_SUPPLIER: (id: string) => `/api/product/${id}/reveal-supplier`,
      EXPORT: '/api/product/export',
      SYNC_SHOPIFY: (id: string) => `/api/product/${id}/sync-shopify`,
      ONLINE_PRODUCTS: '/api/product/online-products',
      ONLINE_PRODUCTS_STATUS: (id: string) => `/api/product/online-products/${id}/status`,
      DESCRIPTION_MANAGEMENT: '/api/product/description-management',
      UPDATE_DESCRIPTION: (id: string) => `/api/product/${id}/description`,
      GENERATE_DESCRIPTION: (id: string) => `/api/product/${id}/description/generate`,
      CATEGORY: '/api/category',
    },

    // Category management
    CATEGORY: {
      LIST: '/api/category',
      HIERARCHY_TREE: '/api/category/hierarchy/tree',
      SEARCH: '/api/category/search',
      DETAILS: (id: string) => `/api/category/${id}`,
      CREATE: '/api/category',
      UPDATE: (id: string) => `/api/category/${id}`,
      DELETE: (id: string) => `/api/category/${id}`,
      EXPORT: '/api/category/export',
      // Public endpoints for purchase quote (no category:view permission required)
      PUBLIC_FOR_PURCHASE_QUOTE: '/api/category/public/for-purchase-quote',
      PUBLIC_HIERARCHY_TREE_FOR_PURCHASE_QUOTE:
        '/api/category/public/hierarchy-tree-for-purchase-quote',
      REVIEW_LIST: '/api/category/approval-requests/categories',
    },

    // Brand management
    BRAND: {
      LIST: '/api/brand',
      OPEN: '/api/brand/open',
      CREATE: '/api/brand',
      CREATE_OPEN: '/api/brand/open',
      UPDATE: (id: string) => `/api/brand/${id}`,
      DELETE: (id: string) => `/api/brand/${id}`,
      DETAILS: (id: string) => `/api/brand/${id}`,
    },

    // Region management
    REGION: {
      LIST: '/api/region',
      DISTRICTS: '/api/region/district',
    },

    // Location management
    LOCATION: {
      LIST: '/api/location',
      TRANSFER_OPTIONS: '/api/location/transfer-options',
      CREATE: '/api/location',
      UPDATE: (id: string) => `/api/location/${id}`,
      DELETE: (id: string) => `/api/location/${id}`,
      DETAILS: (id: string) => `/api/location/${id}`,
      STORE_INVENTORY_SUMMARY: '/api/location/inventory/store-summary',
      STORE_INVENTORY_TIMESERIES: '/api/location/inventory/store-timeseries',
    },

    // Supplier management
    SUPPLIER: {
      LIST: '/api/supplier',
      CREATE: '/api/supplier',
      UPDATE: (id: string) => `/api/supplier/${id}`,
      DELETE: (id: string) => `/api/supplier/${id}`,
      DETAILS: (id: string) => `/api/supplier/${id}`,
      MERGE: '/api/supplier/merge',
      MERGE_LOGS: '/api/supplier/merge-logs',
      EXPORT: '/api/supplier/export',
      CONTACT_RECORDS: (id: string) => `/api/supplier/${id}/contact-records`,
    },

    SUPPLIER_CALLS: {
      LIST: '/api/supplier-calls',
      CREATE: '/api/supplier-calls',
      TOKEN: '/api/supplier-calls/token',
      DETAIL: (id: string) => `/api/supplier-calls/${id}`,
      NOTES: (id: string) => `/api/supplier-calls/${id}/notes`,
      CLIENT_STATUS: (id: string) => `/api/supplier-calls/${id}/client-status`,
      CRM_RESULT: (id: string) => `/api/supplier-calls/${id}/crm-result`,
      RECORDING: (id: string) => `/api/supplier-calls/${id}/recording`,
      SUPPLIERS: '/api/supplier-calls/suppliers',
      PROSPECTS: '/api/supplier-calls/prospects',
      OPERATORS: '/api/supplier-calls/operators',
    },

    WHOLESALE_CUSTOMER: {
      LIST: '/api/wholesale-customer',
      CREATE: '/api/wholesale-customer',
      UPDATE: (id: string) => `/api/wholesale-customer/${id}`,
      DELETE: (id: string) => `/api/wholesale-customer/${id}`,
      DETAILS: (id: string) => `/api/wholesale-customer/${id}`,
      SEARCH: '/api/wholesale-customer/search',
      SEARCH_NAME: '/api/wholesale-customer/search/name',
      SEARCH_COMPANY: '/api/wholesale-customer/search/company',
    },

    ROASTER_RECORDS: {
      LIST: '/api/roaster-records',
      EXPORT: '/api/roaster-records/export',
    },

    // File upload
    UPLOAD: {
      IMAGE: '/api/upload/image',
      FILE: '/api/upload/file',
      DELETE_FILE: '/api/upload/file',
      EXCEL: '/api/upload/excel',
      DOCUMENT: '/api/upload/document',
      DOCUMENTS: '/api/upload/documents',
      FIX_PERMISSIONS: '/api/upload/fix-permissions',
      PRODUCT: {
        IMAGE: (productId: string, imageType: string) =>
          `/api/upload/product/${productId}/image/${imageType}`,
        IMAGES: (productId: string) => `/api/upload/product/${productId}/images`,
        FILES: (productId: string) => `/api/upload/product/${productId}/files`,
        DELETE_FILE: (fileId: string | number) => `/api/upload/product/file/${fileId}`,
        TEMP_IMAGE: {
          FRONT: '/api/upload/product/temp/image/front',
          BACK: '/api/upload/product/temp/image/back',
          BARCODE: '/api/upload/product/temp/image/barcode',
          INNER: '/api/upload/product/temp/image/inner',
          GENERIC: (imageType: string) => `/api/upload/product/temp/image/${imageType}`,
        },
        TEMP_IMAGES: '/api/upload/product/temp/images',
      },
    },

    // Analytics
    ANALYTICS: {
      DASHBOARD: '/api/analytics/dashboard',
      ONLINE_STORE_DASHBOARD: '/api/analytics/online-store/dashboard',
      REPORTS: '/api/analytics/reports',
      PRODUCT_INVENTORY: (productId: string) =>
        `/api/analytics/product-inventory?productId=${productId}`,
      PRODUCT_SPECIFIC_SALES: '/api/analytics/product-specific-sales',
      PRODUCT_SPECIFIC_SALES_EXPORT: '/api/analytics/product-specific-sales/export',
      SUPPLIER_SALES_REPORT: '/api/analytics/supplier-sales-report',
      SUPPLIER_SALES_REPORT_EXPORT: '/api/analytics/supplier-sales-report/export',
      SUPPLIER_SALES_REPORT_METRICS: '/api/analytics/supplier-sales-report/metrics',
    },

    // Market Info (MI)
    MARKET_INFO: {
      PRODUCTS: '/api/mi-products',
      MOBILE: '/api/mobile',
      USERS: '/api/mi-users',
      STORES: '/api/mi-stores',
      BRANDS: '/api/mi-store-brands',
      COLLECTION_TASKS: '/api/mi-collection-tasks',
    },

    // Inventory
    INVENTORY: {
      LIST: '/api/inventory',
      TRANSFER: '/api/inventory/transfer',
      ADJUSTMENT: '/api/inventory/adjustment',
      ADJUST: '/api/inventory/adjust',
      SUMMARY_BY_LOCATION: (locationId: string) => `/api/inventory/summary/location/${locationId}`,
      BY_PRODUCT_AND_LOCATION: (productId: string, locationId: string) =>
        `/api/inventory/product/${productId}/location/${locationId}`,
      BY_PRODUCT: (productId: string) => `/api/inventory/product/${productId}`,
      BY_LOCATION: (locationId: string) => `/api/inventory/products/location/${locationId}`,
      TRANSACTIONS: '/api/inventory/transactions',
      LOSS_SUMMARY: '/api/inventory/loss-summary',
      LOSS_TREND: '/api/inventory/loss-trend',
      LOSS_REASON_BREAKDOWN: '/api/inventory/loss-reason-breakdown',
      LOSS_EVENT_DASHBOARD: '/api/inventory/loss-event-dashboard',
      LOSS_MONTHLY_REPORT: '/api/inventory/loss-monthly-report',
      LOSS_EXCEPTION_REPORT: '/api/inventory/loss-exception-report',
      LOW_STOCK: '/api/inventory/low-stock',
      AUDIT: '/api/inventory/audit',
      PRODUCT_ALL_LOCATIONS: (productId: string) =>
        `/api/inventory/product/${productId}/all-locations`,
      UPDATE_PRODUCT_LOCATION: (productId: string, locationId: string) =>
        `/api/inventory/product/${productId}/location/${locationId}`,
      LOCATIONS: '/api/inventory/locations',
      BATCH_TOTALS: '/api/inventory/batch-totals',
    },

    // Receipt Templates
    RECEIPT: {
      TEMPLATES: '/api/receipt-templates',
      TEMPLATE: (id: string) => `/api/receipt-templates/${id}`,
      CREATE: '/api/receipt-templates',
      UPDATE: (id: string) => `/api/receipt-templates/${id}`,
      DELETE: (id: string) => `/api/receipt-templates/${id}`,
      BY_LOCATION_ACTIVE: (locationId: string) =>
        `/api/receipt-templates/location/${locationId}/active`,
      BY_LOCATION_HISTORY: (locationId: string) =>
        `/api/receipt-templates/location/${locationId}/history`,
      CREATE_DEFAULT: (locationId: string) =>
        `/api/receipt-templates/location/${locationId}/default`,
      SYNC: (locationId: string) => `/api/receipt-templates/location/${locationId}/sync`,
      GENERATE: '/api/receipt/generate',
    },

    // Script management
    SCRIPT: {
      LIST: '/api/script',
      CREATE: '/api/script',
      DETAILS: (id: string | number) => `/api/script/${id}`,
      UPDATE: (id: string | number) => `/api/script/${id}`,
      DELETE: (id: string | number) => `/api/script/${id}`,
      RUN: (identifier: string) => `/api/script/run/${identifier}`,
      RUN_RECORD: '/api/script/run-record',
      RUN_RECORD_BY_SCRIPT: (scriptId: string | number) =>
        `/api/script/run-record/script/${scriptId}`,
      UPDATE_RUN_RECORD: (id: string | number) => `/api/script/run-record/${id}`,
      GROUPS: '/api/script/groups',
      GROUPS_BY_NAME: (groupName: string) => `/api/script/groups/${groupName}`,
      GROUPS_STATUS: '/api/script/groups/status',
      GLOBAL_STATUS: '/api/script/global/status',
    },

    // Enhanced Receipt Templates
    RECEIPT_EXTENDED: {
      BY_LOCATION_ACTIVE: (locationId: string) =>
        `/api/receipt-templates/location/${locationId}/active`,
    },

    // Enhanced Analytics
    ANALYTICS_EXTENDED: {
      DASHBOARD_WITH_DATES: '/api/analytics/dashboard',
    },

    // Location Account management
    LOCATION_ACCOUNT: {
      LIST: '/api/location-account',
      CREATE: '/api/location-account',
      UPDATE: (id: string) => `/api/location-account/${id}`,
      DELETE: (id: string) => `/api/location-account/${id}`,
      DETAILS: (id: string) => `/api/location-account/${id}`,
      CHANGE_PASSWORD: (id: string) => `/api/location-account/${id}/change-password`,
      RESET_PASSWORD: (id: string) => `/api/location-account/${id}/reset-password`,
      UPDATE_STATUS: (id: string) => `/api/location-account/${id}/status`,
    },

    // PDA Account management
    PDA_ACCOUNT: {
      LIST: '/api/pda-account',
      CREATE: '/api/pda-account',
      UPDATE: (id: string) => `/api/pda-account/${id}`,
      DELETE: (id: string) => `/api/pda-account/${id}`,
      DETAILS: (id: string) => `/api/pda-account/${id}`,
      CHANGE_PASSWORD: (id: string) => `/api/pda-account/${id}/change-password`,
      RESET_PASSWORD: (id: string) => `/api/pda-account/${id}/reset-password`,
      UPDATE_STATUS: (id: string) => `/api/pda-account/${id}/status`,
    },

    // Payment Gateway management
    PAYMENT_GATEWAY: {
      LIST: '/api/payment-gateway',
    },

    // Discount management
    DISCOUNT: {
      LIST: '/api/discounts',
      CREATE: '/api/discounts',
      UPDATE: (id: string) => `/api/discounts/${id}`,
      STATUS: (id: string) => `/api/discounts/${id}/status`,
      DELETE: (id: string) => `/api/discounts/${id}`,
      AUDIT_LOGS: (id: string) => `/api/discounts/${id}/audit-logs`,
      CODES: '/api/discounts/codes',
    },

    // POS Update management
    POS_UPDATE: {
      LIST: '/api/pos-update',
      CREATE: '/api/pos-update',
      UPDATE: (id: string) => `/api/pos-update/${id}`,
      DELETE: (id: string) => `/api/pos-update/${id}`,
    },

    PRODUCT_VISIBILITY: {
      BASE: '/api/product-visibility',
      ME: '/api/product-visibility/me',
      USER: (userId: string) => `/api/product-visibility/users/${userId}`,
    },

    // Role management
    ROLE: {
      LIST: '/api/role',
      DETAILS: (id: string | number) => `/api/role/${id}`,
      CREATE: '/api/role',
      UPDATE: (id: string | number) => `/api/role/${id}`,
      DELETE: (id: string | number) => `/api/role/${id}`,
    },

    // Department management
    DEPARTMENT: {
      LIST: '/api/department',
      DETAILS: (id: number) => `/api/department/${id}`,
      CREATE: '/api/department',
      UPDATE: (id: number) => `/api/department/${id}`,
      DELETE: (id: number) => `/api/department/${id}`,
    },

    // FGShop API (Only accessible from FGShop mobile app, not from web frontend)
    FGSHOP: {
      AUTH: {
        LOGIN: '/api/fgshop/auth/login',
        REFRESH: '/api/fgshop/auth/refresh',
        LOGOUT: '/api/fgshop/auth/logout',
        LOGOUT_ALL: '/api/fgshop/auth/logout-all',
        PROFILE: '/api/fgshop/auth/profile',
        LOCATIONS: '/api/fgshop/auth/locations',
        ME: '/api/fgshop/auth/profile', // Alias for profile
      },
      STORES: {
        LIST: '/api/fgshop/stores',
        ANALYTICS: (locationId: string) => `/api/fgshop/stores/${locationId}/analytics`,
        INVENTORY: (locationId: string) => `/api/fgshop/stores/${locationId}/inventory`,
        REPORTS: (locationId: string) => `/api/fgshop/stores/${locationId}/reports`,
        DASHBOARD: '/api/fgshop/stores/dashboard',
      },
      USERS: {
        LIST: '/api/fgshop/users',
        DETAILS: (id: string) => `/api/fgshop/users/${id}`,
        CREATE: '/api/fgshop/users',
        UPDATE: (id: string) => `/api/fgshop/users/${id}`,
        DELETE: (id: string) => `/api/fgshop/users/${id}`,
        RESET_PASSWORD: (id: string) => `/api/fgshop/users/${id}/reset-password`,
        LOGOUT_ALL: (id: string) => `/api/fgshop/users/${id}/logout-all`,
        SESSIONS: (id: string) => `/api/fgshop/users/${id}/sessions`,
        LOCATIONS: '/api/fgshop/users/locations',
      },
    },
    // MI Mobile API
    MI_MOBILE: {
      AUTH: {
        LOGIN: '/api/mi-mobile/auth/login',
        ME: '/api/mi-mobile/auth/me',
        PROFILE: '/api/mi-mobile/auth/me',
        LOGOUT: '/api/mi-mobile/auth/logout',
        CHANGE_PASSWORD: '/api/mi-mobile/auth/change-password',
        VERIFY_TOKEN: '/api/mi-mobile/auth/verify-token',
      },
      TASKS: {
        LIST: '/api/mi-mobile/tasks',
        DETAILS: (id: number) => `/api/mi-mobile/tasks/${id}`,
        START: (id: number) => `/api/mi-mobile/tasks/${id}/start`,
        COMPLETE: (id: number) => `/api/mi-mobile/tasks/${id}/complete`,
      },
      PRODUCTS: {
        PRICES: '/api/mi-mobile/products/prices',
        TASK_PRICES: (taskId: string) => `/api/mi-mobile/products/prices/task/${taskId}`,
        PRICE_DETAIL: (id: string) => `/api/mi-mobile/products/prices/${id}`,
      },
      FILES: {
        UPLOAD: '/api/mi-mobile/upload',
        SECURE: (encryptedUrl: string) => `/api/mi-mobile/files/secure/${encryptedUrl}`,
      },
      ADMIN: {
        BASE: '/api/mi/mobile/admin',
      },
    },

    // Stock Take management
    STOCK_TAKE: {
      LIST: '/api/stock-take',
      CREATE: '/api/stock-take',
      DETAILS: (id: string) => `/api/stock-take/${id}`,
      UPDATE: (id: string) => `/api/stock-take/${id}`,
      DELETE: (id: string) => `/api/stock-take/${id}`,
      // Product management within stock take
      PRODUCTS: {
        LIST: (stockTakeId: string) => `/api/stock-take/${stockTakeId}/products`,
        ADD: (stockTakeId: string) => `/api/stock-take/${stockTakeId}/products`,
        BULK_ADD: (stockTakeId: string) => `/api/stock-take/${stockTakeId}/products/bulk`,
        UPDATE: (stockTakeId: string, productId: string) =>
          `/api/stock-take/${stockTakeId}/products/${productId}`,
        BULK_UPDATE: (stockTakeId: string) => `/api/stock-take/${stockTakeId}/products`,
        DELETE: (stockTakeId: string, productId: string) =>
          `/api/stock-take/${stockTakeId}/products/${productId}`,
      },
      // Operations
      COMPLETE: (id: string) => `/api/stock-take/${id}/complete`,
      SYNC: (id: string) => `/api/stock-take/${id}/sync`,
      REDO_SYNC: (id: string) => `/api/stock-take/${id}/redo-sync`,
    },
  },
};

/**
 * Get full API URL by combining base URL with endpoint
 * @param endpoint - The API endpoint (with or without leading slash)
 * @returns Full API URL
 */
export function getApiUrl(endpoint: string): string {
  const baseURL = API_CONFIG.getBaseURL();

  // If in development and using proxy, return relative URL
  if (!baseURL) {
    return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  }

  // For production/test, return full URL
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseURL}${cleanEndpoint}`;
}

/**
 * Create API client with configured base URL
 * This can be used with any HTTP client (axios, fetch, etc.)
 */
export const createApiClient = () => {
  const baseURL = API_CONFIG.getBaseURL();

  return {
    baseURL,
    get: (url: string) => getApiUrl(url),
    post: (url: string) => getApiUrl(url),
    put: (url: string) => getApiUrl(url),
    delete: (url: string) => getApiUrl(url),
    patch: (url: string) => getApiUrl(url),
  };
};

// Export commonly used configurations
export const { ENDPOINTS } = API_CONFIG;

/**
 * Create a dynamic string-like object that always returns the latest base URL.
 * This keeps backward compatibility with legacy code that imported a constant string.
 */
const createDynamicBaseUrl = () => {
  const getter = () => API_CONFIG.getBaseURL();
  const dynamicString = {
    toString: getter,
    valueOf: getter,
    [Symbol.toPrimitive]: getter,
  };
  return dynamicString as unknown as string;
};

// Export API_BASE_URL for backward compatibility (now dynamic at runtime)
export const API_BASE_URL = createDynamicBaseUrl();

export default API_CONFIG;
